import { useCallback, useMemo } from "react";
import { Stack } from "@/components/layout";
import { SectionHeader, Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { HubEmbeddedProvider, HubTabs, NotConnected, Panel, useHubLoad } from "@/modules/hub-kit/HubKit";
import { formatCount } from "@/modules/hub-kit/format";
import { BroadcastsTab } from "../components/BroadcastsTab";
import { MarketingOverviewTab } from "../components/MarketingOverviewTab";
import { PromoCodesTab } from "../components/PromoCodesTab";
import { createGrowthApi, SEGMENTS, type MarketingOverview } from "../growthApi";
import {
  AudiencesQueuePage,
  CampaignsQueuePage,
  DeliveriesQueuePage,
  TemplatesQueuePage,
} from "./MarketingQueuePages";

const CRUMBS = [{ label: "HUDDL HQ", to: "/app" }, { label: "Marketing" }];

function SegmentsTab({ o }: { o: MarketingOverview | null }) {
  return (
    <Stack gap={24}>
      <Panel
        title="Built-in segments"
        description="Computed from real rows every time, so they are always current. Use them in Broadcasts."
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Segment</Th>
              <Th>Who</Th>
              <Th>In segment</Th>
              <Th>Opted out</Th>
              <Th>Reachable</Th>
            </Tr>
          </Thead>
          <Tbody>
            {SEGMENTS.map((s) => {
              const size = o?.segments[s.key];
              return (
                <Tr key={s.key}>
                  <Td>{s.label}</Td>
                  <Td>{s.description}</Td>
                  <Td>{size ? formatCount(size.in_segment) : "…"}</Td>
                  <Td>{size ? formatCount(size.opted_out) : "…"}</Td>
                  <Td>{size ? formatCount(size.eligible) : "…"}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Panel>
      <Panel title="HQ audiences" description="Hand-built audiences with explicit members and an approval step. An approved or active audience can be a broadcast target.">
        <AudiencesQueuePage />
      </Panel>
    </Stack>
  );
}

function ReferralsTab() {
  return (
    <Stack gap={16}>
      <NotConnected
        title="Referral performance is not charted here yet"
        missing="Play now records a referral (huddlplay://r/<handle>) on the player's next booking and writes commission to creator_payment when that booking is completed and is not a no-show. Withdrawal requests are payout rows in pending_review. This tab does not chart those rows. (Promo codes stay separate.)"
        needs={[
          "A read of booking.referrer_user_id and creator_payment for this tab, so HQ can see referral performance without opening Finance.",
        ]}
        recommendation="Read the rows Play already writes. Do not add a second commission ledger."
      />
      <NotConnected
        title="Push, email and SMS"
        missing="Campaign records can name a push or email channel, but nothing sends them: there is no provider."
        needs={[
          "Push: Play already runs on Expo, so Expo Push Notifications needs no new vendor, only device-token storage and the send worker.",
          "Email: a transactional provider for receipts and announcements.",
          "SMS: a Philippine-capable provider, if SMS is wanted at all.",
        ]}
        recommendation="Push first, through Expo Push (free, already in the stack, reaches iOS and Android). For email, Resend or Postmark, both simple APIs that work from a Cloudflare Worker. Hold SMS until there is a use it is needed for, because it costs per message; Semaphore is the usual Philippine choice. Each needs your decision and an account before it is wired."
      />
    </Stack>
  );
}

/**
 * Marketing: grow supply (businesses, creators) and demand (players).
 */
export function MarketingHubPage() {
  usePageBreadcrumbs(CRUMBS);
  const client = useApiClient();
  const api = useMemo(() => createGrowthApi(client), [client]);
  const load = useCallback(async () => (await api.getOverview()).overview, [api]);
  const overview = useHubLoad(load);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Marketing"
        description="Grow supply and demand: announcements to segments, a record of businesses' promo codes, and governed campaigns."
      />
      <HubEmbeddedProvider crumbs={CRUMBS}>
        <HubTabs
          items={[
            {
              id: "overview",
              label: "Overview",
              content: (
                <MarketingOverviewTab
                  data={overview.data}
                  error={overview.error}
                  loading={overview.loading}
                  reload={() => void overview.reload()}
                />
              ),
            },
            { id: "broadcasts", label: "Announcements", content: <BroadcastsTab /> },
            { id: "promos", label: "Promo codes", content: <PromoCodesTab /> },
            {
              id: "campaigns",
              label: "Campaigns",
              content: (
                <HubTabs
                  param="sub"
                  variant="sub"
                  items={[
                    { id: "campaigns", label: "Campaigns", content: <CampaignsQueuePage /> },
                    { id: "templates", label: "Templates", content: <TemplatesQueuePage /> },
                    { id: "deliveries", label: "Deliveries", content: <DeliveriesQueuePage /> },
                  ]}
                />
              ),
            },
            { id: "segments", label: "Segments", content: <SegmentsTab o={overview.data} /> },
            { id: "referrals", label: "Referrals & attribution", content: <ReferralsTab /> },
          ]}
        />
      </HubEmbeddedProvider>
    </Stack>
  );
}
